/**
 * WhatsApp broadcasts ("Difusiones"): the Meta template each broadcast becomes
 * and the message that goes out to every lead. Pure functions (tested).
 *
 * The team writes the text with {nombre} where the lead's first name goes.
 * Every broadcast carries the same footer and two quick replies: "Me interesa"
 * (brings the lead back to the setters) and "No quiero más mensajes" (opt-out).
 */

export const NAME_TOKEN = "{nombre}";
export const BROADCAST_LANGUAGE = "es";
export const BROADCAST_FOOTER = "Responde BAJA para no recibir más mensajes";
export const INTERESTED_BUTTON = "Me interesa";
export const OPT_OUT_BUTTON = "No quiero más mensajes";
const MAX_BODY = 1024;

export type MediaKind = "image" | "video" | "document";

export interface BroadcastMedia {
  kind: MediaKind;
  url: string;
  fileName?: string | null;
  mimeType?: string | null;
}

/** Problems Meta would reject the template for, in the team's words. */
export const bodyProblems = (body: string): string[] => {
  const text = body.trim();
  const problems: string[] = [];
  if (!text) problems.push("Escribe el texto del mensaje");
  if (text.length > MAX_BODY - 20)
    problems.push("El texto es demasiado largo (máximo unos 1000 caracteres)");
  if (text.split(NAME_TOKEN).length > 2)
    problems.push("Pon {nombre} una sola vez");
  if (text.startsWith(NAME_TOKEN) || text.endsWith(NAME_TOKEN))
    problems.push("El texto no puede empezar ni terminar con {nombre}");
  if (/\{\{\s*\d+\s*\}\}/.test(text)) problems.push("Usa {nombre}, no {{1}}");
  if (/\n\s*\n\s*\n/.test(text))
    problems.push("No dejes más de una línea en blanco seguida");
  return problems;
};

const hasName = (body: string) => body.includes(NAME_TOKEN);

/** Unique, Meta-valid name (lowercase letters, digits and underscores). */
export const templateNameFor = (broadcastId: number, now = Date.now()) =>
  `gula_dif_${broadcastId}_${now.toString(36)}`;

const HEADER_FORMAT: Record<MediaKind, string> = {
  image: "IMAGE",
  video: "VIDEO",
  document: "DOCUMENT",
};

/** What YCloud needs to create the template (POST /whatsapp/templates). */
export const broadcastTemplateDefinition = ({
  wabaId,
  name,
  body,
  media,
}: {
  wabaId: string;
  name: string;
  body: string;
  media?: BroadcastMedia | null;
}) => {
  const text = body.trim().replace(NAME_TOKEN, "{{1}}");
  return {
    wabaId,
    name,
    language: BROADCAST_LANGUAGE,
    category: "MARKETING",
    components: [
      ...(media
        ? [
            {
              type: "HEADER",
              format: HEADER_FORMAT[media.kind],
              example: { header_url: [media.url] },
            },
          ]
        : []),
      {
        type: "BODY",
        text,
        ...(hasName(body) ? { example: { body_text: [["María"]] } } : {}),
      },
      { type: "FOOTER", text: BROADCAST_FOOTER },
      {
        type: "BUTTONS",
        buttons: [
          { type: "QUICK_REPLY", text: INTERESTED_BUTTON },
          { type: "QUICK_REPLY", text: OPT_OUT_BUTTON },
        ],
      },
    ],
  };
};

/** The template part of a send to one lead (POST /whatsapp/messages). */
export const broadcastTemplateMessage = ({
  name,
  body,
  media,
  firstName,
}: {
  name: string;
  body: string;
  media?: BroadcastMedia | null;
  firstName: string;
}) => ({
  name,
  language: { code: BROADCAST_LANGUAGE },
  components: [
    ...(media
      ? [
          {
            type: "header",
            parameters: [
              {
                type: media.kind,
                [media.kind]: {
                  link: media.url,
                  ...(media.kind === "document" && media.fileName
                    ? { filename: media.fileName }
                    : {}),
                },
              },
            ],
          },
        ]
      : []),
    ...(hasName(body)
      ? [{ type: "body", parameters: [{ type: "text", text: firstName }] }]
      : []),
  ],
});

/** The text the lead sees, kept in the CRM conversation. */
export const broadcastText = (body: string, firstName: string) =>
  body.trim().replace(NAME_TOKEN, firstName);

/** Madrid hour (0-23) of a moment. */
export const madridHour = (date: Date) =>
  Number(
    new Intl.DateTimeFormat("en-GB", {
      timeZone: "Europe/Madrid",
      hour: "2-digit",
      hourCycle: "h23",
    }).format(date),
  );

/** Broadcasts only go out from 9:00 to 21:00 (Madrid). */
export const isSendingHour = (date: Date) => {
  const hour = madridHour(date);
  return hour >= 9 && hour < 21;
};
